import { ActivityActorBase } from '#.shared/api/generated/zod/schemas/activityActorBase.zod.ts'
import { ActivityActorType } from '#.shared/api/generated/zod/schemas/activityActorType.zod.ts'
import { ActivityBase } from '#.shared/api/generated/zod/schemas/activityBase.zod.ts'
import { ActivityChangeBase } from '#.shared/api/generated/zod/schemas/activityChangeBase.zod.ts'
import { ActivityChangeType } from '#.shared/api/generated/zod/schemas/activityChangeType.zod.ts'
import { ActivitySnapshotBase } from '#.shared/api/generated/zod/schemas/activitySnapshotBase.zod.ts'
import { ActivityTypeOutput } from '#.shared/api/generated/zod/schemas/activityType.zod.ts'
import { IanaZone, IsoInstant } from '#.shared/dates/types.ts'
import { instantToDateTime } from '#.shared/dates/utils/convert.ts'
import { DateTime } from 'luxon'
import z from 'zod'

import { EmployeeID } from '../../employees/models/employee'

export const ShiftActivityID = z.string().brand<'ShiftActivityID'>()
export type ShiftActivityID = z.infer<typeof ShiftActivityID>

const EntityBase = z.object({
  type: z.union([z.enum(['employee']), z.string()]),
  display_name: z.string(),
})

const EmployeeEntity = EntityBase.extend({
  type: z.literal('employee'),
  id: EmployeeID,
})

const KnownEntity = z.discriminatedUnion('type', [EmployeeEntity])

const UnknownEntity = EntityBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const EntityReference = z.union([KnownEntity, UnknownEntity])
export type EntityReference = z.infer<typeof EntityReference>

/////////////////////////////////////////////////////////////
// Actor
/////////////////////////////////////////////////////////////

export const ActorEmployee = ActivityActorBase.extend({
  type: z.literal('employee' satisfies ActivityActorType),
})

export const ActorSystem = ActivityActorBase.extend({
  type: z.literal('system' satisfies ActivityActorType),
})

export const KnownActor = z.discriminatedUnion('type', [
  ActorEmployee,
  ActorSystem,
])

export const UnknownActor = ActivityActorBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const ActivityActor = z.union([KnownActor, UnknownActor])
export type ActivityActor = z.infer<typeof ActivityActor>

/////////////////////////////////////////////////////////////
// Changes
/////////////////////////////////////////////////////////////

export const ActivityChangeDateTime = ActivityChangeBase.extend({
  type: z.literal('datetime' satisfies ActivityChangeType),
  from: IsoInstant.nullable(),
  to: IsoInstant.nullable(),
}).transform(({ from, to, ...rest }) => ({
  ...rest,
  from: from
    ? instantToDateTime(
        from,
        IanaZone.parse(Intl.DateTimeFormat().resolvedOptions().timeZone),
      )
    : null,
  to: to
    ? instantToDateTime(
        to,
        IanaZone.parse(Intl.DateTimeFormat().resolvedOptions().timeZone),
      )
    : null,
}))

export const ActivityChangeMoney = ActivityChangeBase.extend({
  type: z.literal('money' satisfies ActivityChangeType),
  from: z
    .object({
      amount: z.number(),
      currency: z.string(),
    })
    .nullable(),
  to: z
    .object({
      amount: z.number(),
      currency: z.string(),
    })
    .nullable(),
})

export const ActivityChangeReference = ActivityChangeBase.extend({
  type: z.literal('reference' satisfies ActivityChangeType),
  from: EntityReference.nullable(),
  to: EntityReference.nullable(),
})

export const ActivityChangeReferenceSet = ActivityChangeBase.extend({
  type: z.literal('reference_set' satisfies ActivityChangeType),
  added: z.array(EntityReference),
  removed: z.array(EntityReference),
})

export const ActivityChangeText = ActivityChangeBase.extend({
  type: z.literal('text' satisfies ActivityChangeType),
  from: z.string().nullable(),
  to: z.string().nullable(),
})

export const KnownChange = z.discriminatedUnion('type', [
  ActivityChangeDateTime,
  ActivityChangeMoney,
  ActivityChangeReference,
  ActivityChangeReferenceSet,
  ActivityChangeText,
])

export const UnknownChange = ActivityChangeBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const ActChange = z.union([KnownChange, UnknownChange])
export type ActChange = z.infer<typeof ActChange>

/////////////////////////////////////////////////////////////
// Snapshots
/////////////////////////////////////////////////////////////

export const ActivitySnapshotDateTime = ActivitySnapshotBase.extend({
  type: z.literal('datetime' satisfies ActivityChangeType),
  value: IsoInstant,
}).transform(({ value, ...rest }) => ({
  ...rest,
  value: instantToDateTime(
    value,
    IanaZone.parse(Intl.DateTimeFormat().resolvedOptions().timeZone),
  ),
}))

export const ActivitySnapshotReference = ActivitySnapshotBase.extend({
  type: z.literal('reference' satisfies ActivityChangeType),
  value: EntityReference,
})

export const KnownSnapshot = z.discriminatedUnion('type', [
  ActivitySnapshotDateTime,
  ActivitySnapshotReference,
])

export const UnknownSnapshot = ActivitySnapshotBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const ActivitySnapshot = z.union([KnownSnapshot, UnknownSnapshot])
export type ActivitySnapshot = z.infer<typeof ActivitySnapshot>

/////////////////////////////////////////////////////////////
// Activity
/////////////////////////////////////////////////////////////

const BaseActivity = ActivityBase.extend({
  occurred_at: IsoInstant,
  actor: ActivityActor,
  changes: z.array(ActChange),
  snapshot: z.array(ActivitySnapshot),
})

export const ActivityShiftUpdated = BaseActivity.extend({
  type: z.literal('shift.updated' satisfies ActivityTypeOutput),
})

export const ActivityShiftSkillsChanged = BaseActivity.extend({
  type: z.literal('shift.skills.changed' satisfies ActivityTypeOutput),
})

export const ActivityShiftAssigned = BaseActivity.extend({
  type: z.literal('shift.assignment.assigned' satisfies ActivityTypeOutput),
  details: z.object({
    assigned: EntityReference,
  }),
})

export const ActivityShiftPublished = BaseActivity.extend({
  type: z.literal('shift.published' satisfies ActivityTypeOutput),
})

export const KnownActivity = z.discriminatedUnion('type', [
  ActivityShiftUpdated,
  ActivityShiftSkillsChanged,
  ActivityShiftAssigned,
  ActivityShiftPublished,
])

export const UnknownActivity = BaseActivity.extend({
  type: z.string().refine((t) => t !== 'shift.updated'),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const Activity = z
  .union([KnownActivity, UnknownActivity])
  .transform(({ occurred_at, ...rest }) => ({
    ...rest,
    occurred_at: instantToDateTime(
      occurred_at,
      IanaZone.parse(Intl.DateTimeFormat().resolvedOptions().timeZone),
    ),
  }))
export type Activity = z.infer<typeof Activity>

export type TimelineActivity = Activity

export function parseActivity(input: unknown): TimelineActivity | null {
  const full = Activity.safeParse(input)
  if (full.success) return full.data

  reportContractViolation(full.error, input)

  return null
}

function reportContractViolation(error: Error, input: unknown) {
  console.error('Activity contract violation', error, input)
}

export const ActivityPage = z.object({
  data: z.array(z.unknown()),
  next_cursor: z.string().optional().nullable(),
})

export const ActorType = z.enum(['employee', 'system']) // integration / job
export const ActivitySource = z.enum([
  'web',
  'mobile',
  'api',
  'import',
  'automation',
  'console',
])
export const ActivityCategory = z.enum([
  'lifecycle',
  'change',
  'assignment',
  'offer',
  'queue',
  'comment',
])

export const KnownEvent = z.enum([
  'shift.created',
  'shift.updated',
  'shift.deleted',
  'shift.restored',
  'shift.published',
  'shift.unpublished',
  'shift.cancelled',
  'shift.skills.changed',
  'shift.offer.sent',
  'shift.offer.accepted',
  'shift.offer.declined',
  'shift.offer.expired',
  'shift.offer.withdrawn',
  'shift.assignment.assigned',
  'shift.assignment.unassigned',
  'shift.assignment.reassigned',
  'shift.queue.entered',
  'shift.queue.processed',
  'shift.queue.failed',
])
export type KnownEvent = z.infer<typeof KnownEvent>

export const EntityRef = z.object({
  type: z.string(),
  id: z.union([z.number().int(), z.string()]),
  label: z.string(),
})
export type EntityRef = z.infer<typeof EntityRef>

export const Money = z.object({
  amount: z.number().int(), // minor units
  currency: z.string().length(3),
})

const changeBase = { field: z.string(), label: z.string() }

export const Change = z.discriminatedUnion('kind', [
  z.object({
    ...changeBase,
    kind: z.enum(['string', 'text', 'enum']),
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
  z.object({
    ...changeBase,
    kind: z.enum(['number', 'duration']),
    from: z.number().nullable(),
    to: z.number().nullable(),
  }),
  z.object({
    ...changeBase,
    kind: z.literal('boolean'),
    from: z.boolean().nullable(),
    to: z.boolean().nullable(),
  }),
  z.object({
    ...changeBase,
    kind: z.enum(['datetime', 'date']),
    from: IsoInstant.nullable().transform((s) =>
      s ? DateTime.fromISO(s) : null,
    ),
    to: IsoInstant.nullable().transform((s) =>
      s ? DateTime.fromISO(s) : null,
    ),
    timezone: IanaZone,
  }),
  z.object({
    ...changeBase,
    kind: z.literal('money'),
    from: Money.nullable(),
    to: Money.nullable(),
  }),
  z.object({
    ...changeBase,
    kind: z.literal('relation'),
    from: EntityRef.nullable(),
    to: EntityRef.nullable(),
  }),
  z.object({
    ...changeBase,
    kind: z.literal('collection'),
    added: z.array(EntityRef),
    removed: z.array(EntityRef),
  }),
])

export type Change = z.infer<typeof Change>

export const Actor = z.object({
  type: z.string(), // causer morph alias ('user', 'integration', ...) or 'system'
  id: z.string().nullable(),
  name: z.string(),
  //   avatar_url: z.string().url().nullable().optional(),
  //   is_deleted: z.boolean(),
})
